import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Stack,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  ErrorOutline as ErrorIcon,
  PriorityHigh as PriorityHighIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { formatDateTimeBR } from '../utils/dateUtils';

interface NotificationItem {
  id: string;
  type: 'INTERNAL_ERROR' | 'PRIORITY_URGENT' | 'SYSTEM';
  title: string;
  message: string;
  link?: string | null;
  referenceId?: string | null;
  isRead: boolean;
  createdAt: string;
  readAt?: string | null;
}

const getIconForType = (type: NotificationItem['type']) => {
  switch (type) {
    case 'INTERNAL_ERROR':
      return <ErrorIcon sx={{ color: '#dc2626' }} />;
    case 'PRIORITY_URGENT':
      return <PriorityHighIcon sx={{ color: '#ea580c' }} />;
    default:
      return <InfoIcon sx={{ color: '#2563eb' }} />;
  }
};

const getTypeLabel = (type: NotificationItem['type']) => {
  switch (type) {
    case 'INTERNAL_ERROR':
      return 'Erro Interno';
    case 'PRIORITY_URGENT':
      return 'Prioridade Urgente';
    default:
      return 'Sistema';
  }
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const list = await apiService.getNotifications(200, false);
      setNotifications(list);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleClickNotification = async (n: NotificationItem) => {
    try {
      if (!n.isRead) {
        await apiService.markNotificationAsRead(n.id);
        setNotifications(prev => prev.map(x => (x.id === n.id ? { ...x, isRead: true } : x)));
      }
    } catch {
      // ignora
    }
    if (n.link) {
      navigate(n.link);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // ignora
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {
      // ignora
    }
  };

  const filtered = tab === 'unread' ? notifications.filter(n => !n.isRead) : notifications;
  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <NotificationsIcon sx={{ fontSize: 32, color: '#D4AF37' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Notificações
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} não lida${unreadCount > 1 ? 's' : ''}`}
              color="warning"
              size="small"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchNotifications}
            variant="outlined"
            size="small"
          >
            Atualizar
          </Button>
          {unreadCount > 0 && (
            <Button
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
              variant="contained"
              size="small"
            >
              Marcar todas como lidas
            </Button>
          )}
        </Stack>
      </Stack>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab value="all" label={`Todas (${notifications.length})`} />
          <Tab value="unread" label={`Não lidas (${unreadCount})`} />
        </Tabs>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
              <NotificationsIcon sx={{ fontSize: 56, opacity: 0.3, mb: 1 }} />
              <Typography variant="body1">Nenhuma notificação</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filtered.map((n, idx) => (
                <React.Fragment key={n.id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Tooltip title="Remover">
                        <IconButton edge="end" onClick={(e) => handleDelete(n.id, e)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemButton
                      onClick={() => handleClickNotification(n)}
                      sx={{
                        backgroundColor: n.isRead ? 'transparent' : 'rgba(212, 175, 55, 0.08)',
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ mr: 2, mt: 0.5 }}>{getIconForType(n.type)}</Box>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: n.isRead ? 500 : 700 }}
                            >
                              {n.title}
                            </Typography>
                            <Chip
                              size="small"
                              label={getTypeLabel(n.type)}
                              variant="outlined"
                              sx={{ height: 20, fontSize: 11 }}
                            />
                            {!n.isRead && (
                              <Chip
                                size="small"
                                label="Nova"
                                color="warning"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="div" sx={{ color: 'text.primary', mt: 0.5 }}>
                              {n.message}
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
                              {formatDateTimeBR(n.createdAt)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {idx < filtered.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Notifications;
